import {
    Component, ElementRef, HostListener, OnDestroy,
    OnInit, Renderer2, ViewChild, Output,
    EventEmitter, TemplateRef, AfterViewInit, Input,
    NgZone,
    ViewChildren,
    QueryList,
    HostBinding
} from '@angular/core';
import { AngularDraggableDirective } from '@farris/ui-draggable';
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import {
    CLASS_NAME, DISMISS_REASONS,
    ModalOptions, SnapUtils, TRANSITION_DURATIONS
} from './modal-options.class';
import { BsModalService } from './bs-modal.service';
import { isBs3 } from '@farris/ui-modal/utils';
import { ModalButtonDirective } from './modal-button.directive';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'modal-container',
    template: `
    <div [attr.id]="modalID"

        [ngDraggable]="config.draggable"
        [ngResizable]="config.resizable"
        [rzMinWidth]="config.minWidth"
        [rzMaxWidth]="config.maxWidth"
        [rzMinHeight]="config.minHeight"
        [rzMaxHeight]="config.maxHeight"
        rzHandles="all" [handle]="config.showHeader ? header:dragEl"
        (stopped)="dropdown($event)"
        (rzStop)="resizeStop($event)"
        [inBounds]="true" [bounds]="boundsElement" [rzContainment]="boundsElement" [isFixed]="isModeless"
        [class]="'modal-dialog' + (config.class ? ' ' + config.class : '')" role="document"
        [style.width]="config.width + 'px'" [style.height]="config.fitContent?'auto':(config.height + 'px')"
        style="position: absolute;top: 50%;left: 50%;">
        <div class="modal-content" [ngClass]="{'modal-content--has-header':config.showHeader}">
            <div #header class="modal-header" [style.display]="config.showHeader ? '': 'none'">
                <div class="modal-title">
                    <span *ngIf="config.iconCls" [class]="config.iconCls" style="margin-right: 8px"></span>
                    <span class="modal-title-label">{{ config.title }}</span>
                </div>
                <div class="actions">
                    <ul>
                    <li *ngIf="isModeless&&canSnap"><modal-position-snap class="d-inline-block" style="margin-top:-1px;" [snapBtnCls]="'btn f-btn-icon f-bare'"  [defaultType]="defaultSnapType" (snapClickEvent)="snapClickHandler($event)"  (snapDefaultEvent)="snapDefaultHandler($event)" (snapClearEvent)="snapClearHandler($event)"></modal-position-snap></li>
                        <li *ngIf="config.showMinButton" class="f-btn-icon f-bare">
                            <span class="f-icon modal_minimize"></span></li>
                        <li *ngIf="config.showMaxButton" (click)="changeDialogSize()" class="f-btn-icon f-bare">
                            <span class="f-icon modal_maximize" [class.modalrevert]="isMax"></span>
                        </li>
                        <li *ngIf="config.showCloseButton" (click)="close($event, true)" class="f-btn-icon f-bare">
                             <span class="f-icon modal_close"></span>
                        </li>
                    </ul>
                </div>
            </div>
        <div #dragEl class="f-utils-absolute-all" style="height:10px;z-index: 1;" [style.display]="config.showHeader ? 'none': ''"></div>
            <div class="modal-body" [class.f-utils-flex-column]="dialogType === 'iframe'"   [f-area-response]="config.areaResponse">
                <perfect-scrollbar [config]="{}" [disabled]="!config.enableScroll||config.fitContent"  *ngIf="!dialogType || dialogType === 'default'">
                    <ng-content ></ng-content>
                </perfect-scrollbar>
                <iframe *ngIf="dialogType === 'iframe'" class="f-utils-fill" width="100%" frameborder="0" [src]="iframeSrc"></iframe>
            </div>

            <div [style.textAlign]="config.buttonAlign" class="modal-footer" [ngStyle]="buildFooterStyles()" *ngIf="config.showButtons && config.buttons">
                <ng-container [ngTemplateOutlet]="useButtonsTemplate() ? config.buttons : defaultButtonRef"></ng-container>
            </div>
        </div>
    </div>

    <ng-template #defaultButtonRef>

        <ng-container *ngFor="let btn of config.buttons; last as isLast">
            <button type="button" modal-button  *ngIf="btn.display === undefined && btn.tipsEnable === undefined"
                [class]="btn.cls"
                (click)="btn.handle && btn.handle($event) ">
                <span [class]="btn.iconCls" *ngIf="btn.iconCls"></span> {{ btn.text }}
            </button>

            <button type="button" modal-button  *ngIf="btn.display !== undefined || btn.tipsEnable !== undefined"
                [class]="btn.cls"
                (click)="btn.handle && btn.handle($event) " 
                [style.display]="(btn.display === true || btn.display === undefined) ? '' : 'none'"
                [farrisTooltip]="btn?.tipsEnable" [placement]="'top'" [delay]="500" [content]="btn?.tipsText">
                <span [class]="btn.iconCls" *ngIf="btn.iconCls"></span> {{ btn.text }}
            </button>

        </ng-container>

    </ng-template>
  `,
    host: {
        class: 'modal farris-modal',
        role: 'dialog',
        tabindex: '-1',
        '[attr.aria-modal]': 'true'
    }
})
export class ModalContainerComponent implements OnInit, OnDestroy, AfterViewInit {
    config: ModalOptions;
    isShown = false;
    level: number;
    isAnimated: boolean;
    bsModalService: BsModalService;
    dialogType: string;
    iframeSrc: SafeResourceUrl;
    // 是否非模态窗口
    isModeless = false;
    // 是否已经按照默认Snap类型设置位置 0表示未设置，1表示已设置，-1表示设置被阻断
    hasSetPosByDefaultSnapType = 0;
    @Input() defaultSnapType = '';
    @Input() canSnap = false;
    //当前窗口的位置类型
    private curModalSnap = { snapType: '', snapInfo: null };
    // 弹出窗口与上下左右的间距
    private gapThreshold = 10;

    @Input()
    public set iframe(value: string) {
        if (this.sanitizer) {
            this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(value);
        }
    }
    private isModalHiding = false;
    private originalWidth: number;  // 记录原始的宽度
    private originalHeight: number; // 记录原始的高度

    private prevWidth: number; // 记录上次的宽度
    private prevHeight: number; // 记录上次的高度
    isMax = false;

    @ViewChild(AngularDraggableDirective) draggbar: AngularDraggableDirective;
    @ViewChild('header') modalHeader: ElementRef;
    boundsElement: HTMLElement;

    @Output() closed = new EventEmitter();
    @Output() opened = new EventEmitter();
    @Output() resized = new EventEmitter();

    @HostBinding('id') modalID = `MODALID_${Date.now().toPrecision()}`;

    @ViewChildren(ModalButtonDirective) buttons: QueryList<ModalButtonDirective>;

    dlgHeaderLineHeight: any;
    private _windowResizeToMax = false;
    private windowResize$: Subscription = null;

    private destroy$ = new Subject();
    constructor(
        options: ModalOptions,
        protected el: ElementRef,
        private _renderer: Renderer2,
        private sanitizer: DomSanitizer
    ) {
        this.config = Object.assign({}, options);
        if (!this.config.buttons) {
            this.config.buttons = [
                {
                    text: '取消',
                    cls: 'btn btn-light',
                    iconCls: 'k-icon k-i-close',
                    handle: () => {
                        this.close();
                    }
                },
                {
                    text: '确定',
                    cls: 'btn btn-primary',
                    iconCls: 'k-icon k-i-check',
                    handle: () => { alert('好(｡･∀･)ﾉﾞ嗨哟!!'); }
                }
            ];
        }
        this.setIsModeless();
    }

    ngOnInit(): void {
        if (this.isModeless) {
            // 如果是非模态窗口
            this.boundsElement = null;
            this._renderer.addClass(this.el.nativeElement, 'f-modal-is-modeless');
        } else {
            this.boundsElement = this.el.nativeElement;
        }
        if (this.config.fitContent) {
            this._renderer.addClass(this.el.nativeElement, 'f-modal-fitContent');
        }

        if (this.isAnimated) {
            this._renderer.addClass(this.el.nativeElement, CLASS_NAME.FADE);
        }
        // this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
            this.isShown = true;
            this._renderer.addClass(
                this.el.nativeElement,
                isBs3() ? CLASS_NAME.IN : CLASS_NAME.SHOW
            );
            if (this.isModeless) {
                this._renderer.setStyle(this.el.nativeElement, 'position', 'static');
            }
            this._renderer.setStyle(this.el.nativeElement, 'display', 'block');
            if (this.config.fitContent && this.el.nativeElement.className.indexOf('f-modal-fitContent-scroll') > -1) {
                this.el.nativeElement.scrollTop = 0;
            }

            if (document && document.body) {
                const modalLen = this.bsModalService.getModalsCount();
                if (modalLen === 1) {
                    this.bsModalService.checkScrollbar();
                    this.bsModalService.setScrollbar();
                }
                if (!document.body.classList.contains(CLASS_NAME.OPEN)) {
                    this._renderer.addClass(document.body, CLASS_NAME.OPEN);
                }
            }
            this.opened.emit({ modal: this });
        }, this.isAnimated ? TRANSITION_DURATIONS.BACKDROP : 0);
        // });

        if (this.el.nativeElement) {
            this.el.nativeElement.focus();
        }

        this.dlgHeaderLineHeight = this.getDialogHeaderLineHeight() + 'px';
    }

    ngAfterViewInit(): void {
        // Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
        // Add 'implements AfterViewInit' to the class.
        this._renderer.setStyle(this.el.nativeElement, 'display', 'block');
        // 如果内容适应宽度
        if (this.config.fitContent) {
            let initElRect = this.el.nativeElement.getBoundingClientRect();
            let initBodySize = this.getContainerSize();
            if (initElRect.height < initBodySize.height + 30) {
                this._renderer.addClass(this.el.nativeElement, 'f-modal-fitContent-scroll');
            }
        }

        this.originalHeight = this.config.height;
        this.originalWidth = this.config.width;

        this.windowResize$ = fromEvent(window, 'resize').pipe(
            takeUntil(this.destroy$),
            debounceTime(50)
        ).subscribe((e: any) => {
            // 窗口变更响应
            const winWidth = window.innerWidth - 2 * this.gapThreshold;
            const winHeight = window.innerHeight - 2 * this.gapThreshold;

            let hasSetSnap = this.isModeless && this.curModalSnap['snapType'];
            if (hasSetSnap) {
                this.computePositionBySnapInfo();
            } else {
                if (this.config.width <= winWidth && this.config.height <= winHeight) {
                    if (this._windowResizeToMax) {
                        this.revertDialog();
                        this._windowResizeToMax = false;
                    } else {
                        if (this.isMax) {
                            this.maxDialog(false);
                        }
                    }
                } else {
                    if (!this.isMax) {
                        this._windowResizeToMax = true;
                        this.maxDialog();
                    } else {
                        this.maxDialog(false);
                    }
                }
                this.toCenter();
                this.moveTo(0, 0);
            }

        });

    }

    private getDlgElement() {
        return this.el.nativeElement.querySelector('.modal-dialog');
    }

    private getDialogHeaderLineHeight() {
        if (this.config.showHeader) {
            const modalHeaderStyles = window.getComputedStyle(this.modalHeader.nativeElement);
            const paddingTop = parseInt(modalHeaderStyles.paddingTop.replace('px', ''), 10);
            const paddingBottom = parseInt(modalHeaderStyles.paddingBottom.replace('px', ''), 10);
            return this.config.dialogHeaderHeight - paddingTop - paddingBottom;
        } else {
            return 0;
        }
    }

    // 获取内容区域的尺寸
    getContainerSize() {
        const bodyTarget = this.el.nativeElement.querySelector('.modal-body');
        const height = bodyTarget.offsetHeight;
        const width = bodyTarget.offsetWidth;
        return { width, height };
    }

    buildFooterStyles() {
        let styles = {};

        if (this.config.dialogFooterStyles) {
            styles = Object.assign({}, styles, this.config.dialogFooterStyles);
        }

        return styles;
    }

    useButtonsTemplate() {
        return this.config.buttons instanceof TemplateRef;
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        const tag = event.target as any;

        // if (tag.localName === 'label') {
        //     if (tag.className) {
        //         const clsList = tag.className.split(' ');
        //         if (
        //             clsList.indexOf('k-radio-label') > -1 ||
        //             clsList.indexOf('k-checkbox-label') > -1 ||
        //             clsList.indexOf('custom-control-label') > -1
        //         ) {
        //             return;
        //         }
        //     }
        // }

        // if (tag.localName === 'input' && (tag.type === 'checkbox' || tag.type === 'radio' || tag.type === 'file')) {
        //     return;
        // }

        if (this.config.ignoreBackdropClick || this.config.backdrop === 'static' || event.target !== this.el.nativeElement) {
            event.stopPropagation();
            return;
        }

        this.bsModalService.setDismissReason(DISMISS_REASONS.BACKRDOP);
        this.close();
    }

    @HostListener('window:keydown.esc', ['$event'])
    onEsc(event: any): void {
        if (!this.isShown) {
            return;
        }

        if (event.keyCode === 27) {
            event.preventDefault();
        }
        /**从最后一个窗口开始关闭 */
        if (
            this.config.keyboard &&
            this.level === this.bsModalService.getModalsCount()
        ) {
            this.bsModalService.setDismissReason(DISMISS_REASONS.ESC);
            this.close(event);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.isShown) {
            this.close();
        }

        if (this.windowResize$) {
            this.windowResize$.unsubscribe();
            this.windowResize$ = null;
        }
    }

    close(e?: MouseEvent, isCloseButton = false): void {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }

        if (this.isModalHiding || !this.isShown) {
            return;
        }

        this.config.beforeClose(this, { event: e }).subscribe(r => {
            if (!r) {
                return false;
            }
            // 更新服务中非模态窗口的个数
            this.bsModalService.closeUpdateModelessCountByModal(this.isModeless);
            this.isModalHiding = true;
            this._renderer.removeClass(
                this.el.nativeElement,
                isBs3() ? CLASS_NAME.IN : CLASS_NAME.SHOW
            );
            setTimeout(() => {
                this.isShown = false;
                this.bsModalService.hide(this.level);
                this.isModalHiding = false;

                if (document && document.body && !document.querySelectorAll('.farris-modal.show').length) {
                    this._renderer.removeClass(document.body, CLASS_NAME.OPEN);
                }

                if (this.config.closed) {
                    this.config.closed(isCloseButton, { modalRef: this, event: e });
                }
                this.closed.emit(this.level);
            }, this.isAnimated ? TRANSITION_DURATIONS.MODAL : 0);
            // });
        });
    }
    /**
     * 通过设置let、top让窗口移动到中间位置
     * 直接调整了
     */
    toCenter() {
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        const contentEl = this.el.nativeElement.querySelector('.modal-body');
        // 解决初始时调用toCenter方法居中
        if (this.hasSetPosByDefaultSnapType == -1) {
            //按照默认Snap类型调整过位置
            this.computePositionBySnapInfo();
            this.hasSetPosByDefaultSnapType = 1;
        } else {
            const marginLeft = Number.parseInt('' + -this.config.width / 2);
            const marginTop = Number.parseInt('' + -this.config.height / 2);
            // this._renderer.setStyle(dialogEl, 'margin-left', marginLeft);
            // this._renderer.setStyle(dialogEl, 'margin-top', marginTop);
            this._renderer.setStyle(dialogEl, 'left', `${window.innerWidth / 2 + marginLeft}px`);
            this._renderer.setStyle(dialogEl, 'top', `${window.innerHeight / 2 + marginTop}px`);
        }

        let contentHeight = this.config.height - 38;
        if (this.config.showButtons) {
            contentHeight = contentHeight - 50;
        }
        if (!this.config.fitContent) {
            this._renderer.setStyle(
                contentEl,
                'height',
                contentHeight + 'px'
            );
        }
        this._renderer.setStyle(contentEl, 'overflow', 'hidden');
        this._renderer.setStyle(dialogEl, 'transform', `translate3d(0px, 0px, 0px)`);
        setTimeout(() => {
            const container = this.getContainerSize();
            const { width: containerWidth, height: containerHeight } = container;
            this.resized.emit({ width: this.config.width, height: this.config.height, containerWidth, containerHeight });
        });
    }
    /**
     * 两处使用此方法，只有moveTo(0,0)，重置拖拽用到的属性transform
     * @param x 
     * @param y 
     */
    moveTo(x: number, y: number) {
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        this._renderer.setStyle(dialogEl, 'transform', `translate3d(${x}px, ${y}px, 0px)`);
        // this._renderer.setStyle(dialogEl, 'top', '50%');
        // this._renderer.setStyle(dialogEl, 'left', '50%');

        // const marginLeft = Number.parseInt('' + -this.config.width / 2);
        // const marginTop = Number.parseInt('' + -this.config.height / 2);
        // this._renderer.setStyle(dialogEl, 'left', `${window.innerWidth / 2 + marginLeft}px`);
        // this._renderer.setStyle(dialogEl, 'top',  `${window.innerHeight / 2 + marginTop}px`);


        this.draggbar.position = { x, y };
        this.draggbar.resetPosition();
    }

    maxDialog(rememberPrevSize = true) {
        if (rememberPrevSize) {
            this.prevWidth = this.config.width;
            this.prevHeight = this.config.height;
        }
        // this.config.width = (document.scrollingElement?document.scrollingElement:document.documentElement)['clientWidth'] - 20;
        // this.config.height = (document.scrollingElement?document.scrollingElement:document.documentElement)['clientHeight'] - 20;

        this.config.width = window.innerWidth - 2 * this.gapThreshold;
        this.config.height = window.innerHeight - 2 * this.gapThreshold;

        this.config.draggable = false;
        this.config.resizable = false;
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        this._renderer.addClass(dialogEl, 'model-dialog-maximize');
        this._renderer.setStyle(dialogEl, 'top', '50%');
        this._renderer.setStyle(dialogEl, 'left', '50%');
        this.isMax = true;
        this.toCenter();
        this.moveTo(0, 0);
    }

    resize(width: number, height: number) {
        if (width) {
            this.config.width = width;
        }

        if (height) {
            this.config.height = height;
        }

        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        this._renderer.removeClass(dialogEl, 'model-dialog-maximize');
        this.toCenter();
        this.draggbar.resetPosition();
    }

    dropdown(el: any) {
        // const rect = el.getBoundingClientRect();
        // this._renderer.removeStyle(el, 'margin-top');
        // this._renderer.removeStyle(el, 'margin-left');
        // this._renderer.setStyle(el, 'top', `${rect.top}px`);
        // this._renderer.setStyle(el, 'left', `${rect.left}px`);

        // this._renderer.removeStyle(el, 'transform');
        // this._renderer.removeStyle(el, '-webkit-transform');
        // this._renderer.removeStyle(el, '-ms-transform');
        // this._renderer.removeStyle(el, '-moz-transform');
        // this._renderer.removeStyle(el, '-o-transform');
    }

    revertDialog() {
        this.isMax = false;
        let hasSetSnap = this.isModeless && this.curModalSnap['snapType'];
        const ww = window.innerWidth - 2 * this.gapThreshold;
        const wh = window.innerHeight - 2 * this.gapThreshold;
        if (!hasSetSnap) {
            this.config.width = this.prevWidth == ww ? this.originalWidth : this.prevWidth;
            this.config.height = this.prevHeight == wh ? this.originalHeight : this.prevHeight;

            if (this.config.width > ww) {
                this.config.width = ww;
            }

            if (this.config.height > wh) {
                this.config.height = wh;
            }
        }
        /**
         * 2024年3月19日
         * 这个地方处理的可能有问题
         * 如果配置文件设置的不可拖拽、不可更改这样此处就有问题了
         */
        this.config.draggable = true;
        this.config.resizable = true;
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        this._renderer.removeClass(dialogEl, 'model-dialog-maximize');
        if (hasSetSnap) {
            this.computePositionBySnapInfo();
        } else {
            this.toCenter();
        }
        this.draggbar.resetPosition();
    }

    changeDialogSize() {
        if (this.isMax) {
            this.revertDialog();
        } else {
            this.maxDialog();
        }
        this._windowResizeToMax = false;
    }
    /**
     * 调整大小
     * @param opts 
     */
    resizeStop(opts: any) {
        this.config.width = opts.size.width;
        this.config.height = opts.size.height;
        const container = this.getContainerSize();
        const { width: containerWidth, height: containerHeight } = container;
        this.resized.emit({ ...opts.size, ...{ containerWidth, containerHeight } });
    }
    /**
     * 是否非模态
     * @returns 
     */
    setIsModeless() {
        if (this.config.hasOwnProperty('modeless')) {
            this.isModeless = this.config.modeless + '' == 'true';
            if (this.isModeless&&this.config.hasOwnProperty('canSnap')) {
                this.canSnap = this.config.canSnap + '' == 'true';
            }
            if (this.canSnap) {
                this.defaultSnapType = this.config.hasOwnProperty('defaultSnapType') ? this.config['defaultSnapType'] : '';
            }
        } else {
            this.isModeless = false;
        }
    }
    updateDialogZindex(zIndex) {
        if (this.isModeless) {
            let modalEl = this.el.nativeElement;
            const dialogEl = modalEl.querySelector('.modal-dialog');
            this._renderer.setStyle(dialogEl, 'position', 'fixed');
            this._renderer.setStyle(dialogEl, 'zIndex', zIndex ? zIndex : modalEl.style.zIndex);
        }
    }
    /**
     * 缩略图点击
     * 留有间距 -宽、高都减少gapThreshold，定位点，也减少gapThreshold
     * @param ev 
     */
    snapClickHandler(snapEv) {
        this.handleSnapClickEvent(snapEv, false);
    }
    /**
     * 清空snap事件
     * @param snapEv 
     */
    snapClearHandler(snapEv) {
        this.curModalSnap['snapType'] = "";
        this.curModalSnap['snapInfo'] = null;
        this.computeWidthOrHeight('Width');
        this.computeWidthOrHeight('Height');
        this.config.draggable = true;
        this.config.resizable = true;
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        this._renderer.removeClass(dialogEl, 'model-dialog-maximize');
        this.toCenter();
        this.draggbar.resetPosition();
    }
    /**
     * 设置默认值
     * @param snapEv 
     */
    snapDefaultHandler(snapEv) {
        this.handleSnapClickEvent(snapEv, true);
    }
    /**
     * 是否来自默认
     * @param fromDefault 
     */
    private handleSnapClickEvent(snapEv, fromDefault = false) {
        // 当前位置类型
        this.curModalSnap['snapType'] = snapEv['snapType'];
        this.curModalSnap['snapInfo'] = snapEv['snapInfo'];
        if (fromDefault) {
            // 捕获时机太早，还没有初始化结束
            this.hasSetPosByDefaultSnapType = -1;
        } else {
            this.computePositionBySnapInfo();
        }
    }
    private computeWidthOrHeight(widthOrHeight: string) {
        let tPopValue = this.curModalSnap['resize' + widthOrHeight];
        tPopValue = tPopValue ? tPopValue : (this['original' + widthOrHeight] ? this['original' + widthOrHeight] : 400);
        const wPropValue = window['inner' + widthOrHeight] - 2 * this.gapThreshold;
        tPopValue = Math.min(tPopValue, wPropValue);
        this.config[widthOrHeight.toLocaleLowerCase()] = tPopValue;
        this.curModalSnap['resize' + widthOrHeight] = tPopValue;
    }
    /**
     * 根据Snap信息计算弹窗应在的位置
     */
    private computePositionBySnapInfo() {
        let calPos = SnapUtils.getCalculateSize(0, 0, this.gapThreshold, this.curModalSnap['snapInfo']);
        const dialogEl = this.el.nativeElement.querySelector('.modal-dialog');
        if (dialogEl) {
            for (var key in calPos) {
                if (key == 'width' || key == 'height') {
                    this.config[key] = calPos[key];
                } else {
                    this._renderer.setStyle(dialogEl, key, calPos[key] == 'auto' ? 'auto' : calPos[key] + 'px');
                }
            }
            this._renderer.setStyle(dialogEl, 'transform', `translate3d(0px, 0px, 0px)`);
        }
    }
}
